import type Stripe from 'stripe';

// Helper function to get environment variable from secrets or process.env
const getEnvVar = (varName: string): string | undefined => {
  // First try to get from window.REACT_APP_SECRETS (injected by server)
  if (typeof window !== 'undefined' && (window as any).REACT_APP_SECRETS?.[varName]) {
    return (window as any).REACT_APP_SECRETS[varName];
  }

  // Fallback to process.env
  return process.env[varName];
};

// Stripe publishable key - must be set in environment variables
const stripePublishableKey = getEnvVar('REACT_APP_STRIPE_PUBLISHABLE_KEY');

if (!stripePublishableKey) {
  console.error('Stripe publishable key is missing. Please set REACT_APP_STRIPE_PUBLISHABLE_KEY in your Render Dashboard or upload as secret file.');
}

// Stripe configuration
export const STRIPE_CONFIG = {
  publishableKey: stripePublishableKey,
  // Product IDs from your Stripe account
  products: {
    starter: 'prod_T2yWz57hnulwNS', // Starter package product ID
  },
  // Price IDs from your Stripe account
  prices: {
    starter_monthly: 'price_1S6sZsBMYLOLDL2LC6ostRQp', // $27/month
    starter_usage: 'price_1S6sOlBMYLOLDL2L7zVAHurU', // $27/month usage-based
  }
};

// Pricing plans configuration
export interface PricingPlan {
  id: string;
  name: string;
  description: string;
  type: 'subscription' | 'credits' | 'free';
  priceId?: string;
  productId?: string;
  paymentLink?: string;
  credits?: number;
  price: {
    monthly: number;
    yearly: number;
  };
  features: string[];
  limitations: string[];
  popular?: boolean;
}

export const PRICING_PLANS: PricingPlan[] = [
  {
    id: 'free',
    name: 'Free',
    description: 'Perfect for trying out our service',
    type: 'free',
    credits: 1,
    price: {
      monthly: 0,
      yearly: 0,
    },
    features: [
      '1 free analysis credit',
      'Basic 3000+ features extraction',
      'Standard quality reports',
      'Advanced AI authenticity detection',
      "Boost your content with AI Recommendations",
      "Viral prediction engine",
      "Competitor analysis",
      "Multi-platform optimization",
      'Email support',
      'Basic trend analysis',
    ],
    limitations: [
      'Limited to 1 credit per month',
      'No advanced AI features',
      'Futures features access'
    ],
  },
  {
    id: 'starter',
    name: 'Starter',
    description: 'Perfect for individual creators',
    type: 'credits',
    paymentLink: 'https://buy.stripe.com/7sYcN4fIf9pM1vG3JoaAw00',
    credits: 100, // 100 credits per month
    price: {
      monthly: 27, // $20/month for 100 credits (based on usage)
      yearly: 270, // $200/year (save $40)
    },
    features: [
      '100 analysis credits per month',
      'Complete 3000+ features extraction',
      'Advanced AI authenticity detection',
      'Boost your content with AI Recommendations',
      'Viral prediction engine',
      'Competitor analysis',
      'Multi-platform optimization',
      'Priority email support',
      'Advanced trend analysis',
      'Futures features access',
    ],
    limitations: [
      'Limited to 100 credits per month',
      'No white-label solutions',
      'No dedicated support',
    ],
    popular: true,
  },
  {
    id: 'pro',
    name: 'Pro',
    description: 'For Market researchers',
    type: 'subscription',
    paymentLink: 'https://buy.stripe.com/9B65kC2VtfOagqA6VAaAw01',
    credits: 1000, // 100 credits per month
    price: {
      monthly: 100, // $27/month unlimited
      yearly: 1000, // $270/year (save $54)
    },
    features: [
      'Data Drop with 1000+ videos with custom features',
      '1000 analysis credits',
      'Complete 3000+ features extraction',
      'Advanced AI authenticity detection',
      'Viral prediction engine',
      'Competitor analysis',
      'Custom branded reports',
      'Boost your content with AI Recommendations',
      'Multi-platform optimization',
      'Priority email support',
      'Advanced trend analysis',
      'Futures features access',
    ],
    limitations: [],
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    description: 'For Enterprise Market researchers',
    type: 'subscription',
    price: {
      monthly: 0,
      yearly: 0,
    },
    features: [
      'Everything in Pro',
      'Custom Data Drop',
      'Custom Features',
      'Custom Branding',
      'Custom Reporting',
      'Custom AI Model Training',
      'Custom Integrations',
      'Custom Training',
      'Custom Support',
    ],
    limitations: [],
  },
];

// Credit system configuration
export const CREDIT_CONFIG = {
  // Cost per LLM run in credits
  costPerRun: 1,
  // Free tier monthly credits
  freeTierCredits: 1,
  
  // Video Agent configuration
  // Note: Free users are limited by monthly query counts, NOT by credit deductions
  // These values are kept for documentation/display purposes only
  videoAgent: {
    chatQueryCost: 0.1,     // Display value (not deducted)
    searchQueryCost: 0.05,   // Display value (not deducted)
    unlimitedThreshold: 100, // Users with >100 credits get unlimited Video Agent access
  },
  
  // Monthly limits for free users (credits < 100, no active subscription)
  freeTierLimits: {
    chatQueries: 5,          // Max 5 chat queries per month
    indexSearches: 20,       // Max 20 index searches per month
  },
  
  // Credit packages
  packages: {
    starter: 100,
    pro: 1000
  },
};

// Helper functions
/** Legacy promo-plan users (redeem_promo_code RPC); not shown on /pricing. */
const PROMO_PLAN: PricingPlan = {
  id: 'promo',
  name: 'Promo',
  description: 'Credits unlocked via promotional code',
  type: 'credits',
  credits: 0,
  price: { monthly: 0, yearly: 0 },
  features: [
    'Bonus credits from a promo code',
    'Same analysis features as paid credit plans while credits last',
  ],
  limitations: ['No recurring subscription — add credits or upgrade for more'],
};

export const getPlanById = (planId: string): PricingPlan | undefined => {
  if (planId === 'promo') return PROMO_PLAN;
  return PRICING_PLANS.find(plan => plan.id === planId);
};

export const calculateCreditsNeeded = (runs: number): number => {
  return runs * CREDIT_CONFIG.costPerRun;
};

export const canAffordRun = (currentCredits: number): boolean => {
  return currentCredits >= CREDIT_CONFIG.costPerRun;
};

// ===========================
// Stripe Customer Management
// ===========================

/**
 * Customer metadata configuration
 * Keys that we store in Stripe customer metadata for easy lookup
 */
export const CUSTOMER_METADATA_KEYS = {
  FIREBASE_UID: 'firebase_uid',
  PLATFORM: 'platform',
  CREATED_VIA: 'created_via',
  CREATED_AT: 'created_at',
  COMPANY: 'company',
  JOB_TITLE: 'job_title',
  USER_TIER: 'user_tier',
} as const;

/**
 * Customer-related helper functions
 */
export const CustomerHelpers = {
  /**
   * Generate customer description
   */
  generateDescription: (firebaseUid: string, email: string): string => {
    return `Mentis AI User - ${email} (${firebaseUid})`;
  },

  /**
   * Generate default customer metadata
   */
  generateMetadata: (firebaseUid: string, additionalData?: Record<string, string>): Record<string, string> => {
    return {
      [CUSTOMER_METADATA_KEYS.FIREBASE_UID]: firebaseUid,
      [CUSTOMER_METADATA_KEYS.PLATFORM]: 'mentis_ai',
      [CUSTOMER_METADATA_KEYS.CREATED_VIA]: 'auth_service',
      [CUSTOMER_METADATA_KEYS.CREATED_AT]: new Date().toISOString(),
      ...additionalData,
    };
  },

  /**
   * Parse customer metadata to extract user info
   */
  parseMetadata: (metadata: Record<string, string>): {
    firebaseUid?: string;
    platform?: string;
    createdVia?: string;
    createdAt?: string;
    company?: string;
    jobTitle?: string;
  } => {
    return {
      firebaseUid: metadata[CUSTOMER_METADATA_KEYS.FIREBASE_UID],
      platform: metadata[CUSTOMER_METADATA_KEYS.PLATFORM],
      createdVia: metadata[CUSTOMER_METADATA_KEYS.CREATED_VIA],
      createdAt: metadata[CUSTOMER_METADATA_KEYS.CREATED_AT],
      company: metadata[CUSTOMER_METADATA_KEYS.COMPANY],
      jobTitle: metadata[CUSTOMER_METADATA_KEYS.JOB_TITLE],
    };
  },
};

/**
 * Stripe webhook event types that we handle
 */
export const STRIPE_WEBHOOK_EVENTS = {
  CUSTOMER_CREATED: 'customer.created',
  CUSTOMER_UPDATED: 'customer.updated',
  CUSTOMER_DELETED: 'customer.deleted',
  SUBSCRIPTION_CREATED: 'customer.subscription.created',
  SUBSCRIPTION_UPDATED: 'customer.subscription.updated',
  SUBSCRIPTION_DELETED: 'customer.subscription.deleted',
  PAYMENT_SUCCEEDED: 'invoice.payment_succeeded',
  PAYMENT_FAILED: 'invoice.payment_failed',
  CHECKOUT_COMPLETED: 'checkout.session.completed',
} as const;

// Export Stripe type for use in other modules
export type { Stripe };
